import PropTypes from 'prop-types';
import Model from './model';
import Event from './event';
import { faCloudUpload, faClipboardList, faPalette, faUsers, faBullhorn, faCalculatorAlt, faCogs, faArchive, faChartLine } from '@fortawesome/pro-solid-svg-icons';

// Mixins / Modules
import Window from '../../modules/window';

export const opportunityShape = PropTypes.shape({
  id: PropTypes.number,
  published: PropTypes.bool,
  published_at: PropTypes.string,
  closed_at: PropTypes.string,
  location: PropTypes.string,
  title: PropTypes.string,
  updated_at: PropTypes.string,
  questionnaire_id: PropTypes.number,
  client_request_id: PropTypes.number,
  is_closed: PropTypes.bool,
});

export default class Opportunity extends Model {
  static NAME = 'opportunity';
  static COLLECTION_NAME = "opportunities";
  static URL = '/api/v2/opportunities';

  static contractTypes = {
    'full_time': 'Full Time',
    'part_time': 'Part Time'
  };

  static directHires = {
    'contract': 'Contract',
    'permanent': 'Permanent'
  };

  static categories = {
    it: 'IT',
    management: 'Management',
    design: 'Design',
    hr: 'HR',
    marketing: 'Marketing',
    finance: 'Finance',
    operations: 'Operations',
    sales: 'Sales',
    other: 'Other',
  };

  static categoryIcons = {
    it: faCloudUpload,
    management: faClipboardList,
    design: faPalette,
    hr: faUsers,
    marketing: faBullhorn,
    finance: faCalculatorAlt,
    operations: faCogs,
    sales: faChartLine,
    other: faArchive,
  };

  static workTypes = {
    'on-site': 'On Site',
    'hybrid': 'Hybrid',
    'remote': 'Remote Only',
  }

  // == Instance Methods =====================================================
  isPublished() {
    return !!this.published_at;
  }

  trackView() {
    const data = {
      event_type: 'opportunity_viewed',
      event_data: JSON.stringify({
        client_request_id: this.client_request_id,
        opportunity_id: this.id,
        opportunity_title: this.title,
      }),
      description: 'Viewed Opportunity',
    };
    (new Event(data)).save()
  }

  canShare() {
    return (!this.is_closed && this.published);
  }

  isInternal() {
    return !!this.client_request_id;
  }

  companyName() {
    return this.company_display_name;
  }

  companyLogo() {
    return this.company_display_logo;
  }

  shareText() {
    return `${this.companyName()} has posted a ${this.title} opportunity`;
  }

  shareUrl() {
    return `${window.location.origin}/opportunities/${this.id}`;
  }

  encodedShareUrl() {
    return `${this.shareUrl()}`.replace(':', '%3A').replaceAll('/', '%2F');
  }

  shareFacebookUrl() {
    return `http://www.facebook.com/sharer/sharer.php?u=${this.encodedShareUrl()}`;
  }

  shareLinkedinUrl() {
    return `https://www.linkedin.com/cws/share?url=${this.encodedShareUrl()}`;
  }

  shareTwitterUrl() {
    return `https://twitter.com/intent/tweet?text=${this.shareText()}&url=${this.encodedShareUrl()}`;
  }

  aiData() {
    return {
      id: this.id,
      description_html: this.description_html,
      title: this.title,
      city: this.city,
      province_state: this.province_state,
      country: this.country,
      page_title: this.page_title,
      meta_description: this.meta_description,
    };
  }

  callAiDescription(url) {
    if (this.description_html) {
      return (new Opportunity(this.aiData()))
        .url(url)
        .post();
    } else {
      Window.flash.error('Description Required')
      return new Promise();
    }
  }

  generateDescriptionHtml() {
    // Not using callAiDescription here because blank description is allowed
    return (new Opportunity(this.aiData()))
      .url(`/api/v2/client_requests/${this.client_request_id}/opportunities/ai_description`)
      .post();
  }

  regenerateDescriptionHtml() {
    return this.callAiDescription(`/api/v2/client_requests/${this.client_request_id}/opportunities/ai_description`);
  }

  spellcheckDescriptionHtml() {
    return this.callAiDescription(`/api/v2/client_requests/${this.client_request_id}/opportunities/ai_description/spellcheck`);
  }

  reduceDescriptionHtml() {
    return this.callAiDescription(`/api/v2/client_requests/${this.client_request_id}/opportunities/ai_description/reduce`);
  }

  extendDescriptionHtml() {
    return this.callAiDescription(`/api/v2/client_requests/${this.client_request_id}/opportunities/ai_description/extend_description`);
  }
}
